import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../services/api';
import { useAuthStore } from '../store/authStore';
import { ArrowLeft, Send, Settings, User, Lock, Calendar as CalendarIcon, Copy, RefreshCw, Users2, Trash2, KeyRound, Link2, ExternalLink } from 'lucide-react';

type Tab = 'profile' | 'password' | 'telegram' | 'ical' | 'family' | 'eltern_portal';

interface Profile {
  id: string; email: string; name: string; role: string; class: string | null; email_verified: boolean;
}
interface TelegramInfo { chat_id: string | null; notifications_enabled: boolean; }
interface IcsInfo { token: string | null; url: string | null; webcal_url: string | null; }

function getTabs(role: string | undefined): { key: Tab; label: string; icon: React.ReactNode }[] {
  const base: { key: Tab; label: string; icon: React.ReactNode }[] = [
    { key: 'profile', label: 'Profil', icon: <User className="w-4 h-4" /> },
    { key: 'password', label: 'Passwort', icon: <Lock className="w-4 h-4" /> },
    { key: 'family', label: 'Eltern / Kind', icon: <Users2 className="w-4 h-4" /> },
  ];
  if (role === 'parent' || role === 'admin') {
    base.push({ key: 'eltern_portal', label: 'Eltern-Portal', icon: <Link2 className="w-4 h-4" /> });
  }
  base.push(
    { key: 'telegram', label: 'Telegram', icon: <Send className="w-4 h-4" /> },
    { key: 'ical', label: 'Kalender-Abo', icon: <CalendarIcon className="w-4 h-4" /> },
  );
  return base;
}

function Feedback({ msg }: { msg: { kind: 'ok' | 'err'; text: string } | null }) {
  if (!msg) return null;
  return (
    <div className={`rounded-lg px-4 py-3 text-sm mt-4 ${msg.kind === 'ok' ? 'bg-green-500/10 border border-green-500/40 text-green-300' : 'bg-red-500/10 border border-red-500/40 text-red-300'}`}>
      {msg.text}
    </div>
  );
}

const ProfileTab: React.FC = () => {
  const [p, setP] = useState<Profile | null>(null);
  const [name, setName] = useState('');
  const [klass, setKlass] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ kind: 'ok' | 'err'; text: string } | null>(null);

  useEffect(() => {
    (async () => {
      try {
        const r = await api<{ user: Profile }>('/api/me/profile');
        setP(r.user); setName(r.user.name); setKlass(r.user.class ?? '');
      } catch (err: any) { setMsg({ kind: 'err', text: err?.message || 'Fehler' }); }
    })();
  }, []);

  const save = async (e: React.FormEvent) => {
    e.preventDefault(); setBusy(true); setMsg(null);
    try {
      const r = await api<{ user: Profile }>('/api/me/profile', {
        method: 'PUT', body: JSON.stringify({ name, class: klass || null }),
      });
      setP(r.user); setMsg({ kind: 'ok', text: 'Profil aktualisiert.' });
      // Update the cached user in authStore so name in header refreshes
      const auth = useAuthStore.getState();
      if (auth.user) {
        const updatedUser = { ...auth.user, name: r.user.name };
        localStorage.setItem('user', JSON.stringify(updatedUser));
        useAuthStore.setState({ user: updatedUser });
      }
    } catch (err: any) { setMsg({ kind: 'err', text: err?.message || 'Fehler' }); }
    finally { setBusy(false); }
  };

  if (!p) return <p className="text-gray-500">Lädt…</p>;

  return (
    <form onSubmit={save} className="space-y-4">
      <div>
        <label className="block text-sm text-gray-300 mb-1">E-Mail</label>
        <input type="email" value={p.email} disabled
          className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-gray-500" />
        <p className="text-xs text-gray-500 mt-1">Die E-Mail-Adresse zu ändern ist derzeit nicht möglich.</p>
      </div>
      <div>
        <label className="block text-sm text-gray-300 mb-1">Rolle</label>
        <input type="text" value={p.role} disabled
          className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-gray-500 capitalize" />
      </div>
      <div>
        <label className="block text-sm text-gray-300 mb-1">Name</label>
        <input type="text" value={name} onChange={e => setName(e.target.value)}
          className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-blue-500" />
      </div>
      <div>
        <label className="block text-sm text-gray-300 mb-1">Klasse</label>
        <input type="text" value={klass} onChange={e => setKlass(e.target.value)} placeholder="z. B. 7g, 10A"
          className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-blue-500" />
        <p className="text-xs text-gray-500 mt-1">Die erste Zahl der Klasse bestimmt deine Jahrgangsstufe — Termine werden dahingehend gefiltert.</p>
      </div>
      <button type="submit" disabled={busy} className="bg-blue-600 hover:bg-blue-700 disabled:bg-gray-600 text-white font-semibold px-4 py-2 rounded-lg">
        {busy ? 'Speichert…' : 'Speichern'}
      </button>
      <Feedback msg={msg} />
    </form>
  );
};

const PasswordTab: React.FC = () => {
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ kind: 'ok' | 'err'; text: string } | null>(null);

  const save = async (e: React.FormEvent) => {
    e.preventDefault();
    if (next.length < 8) { setMsg({ kind: 'err', text: 'Neues Passwort muss ≥ 8 Zeichen haben.' }); return; }
    if (next !== confirm) { setMsg({ kind: 'err', text: 'Passwörter stimmen nicht überein.' }); return; }
    setBusy(true); setMsg(null);
    try {
      await api('/api/me/password', {
        method: 'PUT', body: JSON.stringify({ current_password: current, new_password: next }),
      });
      setCurrent(''); setNext(''); setConfirm('');
      setMsg({ kind: 'ok', text: 'Passwort geändert.' });
    } catch (err: any) { setMsg({ kind: 'err', text: err?.message || 'Fehler' }); }
    finally { setBusy(false); }
  };

  return (
    <form onSubmit={save} className="space-y-4">
      <div>
        <label className="block text-sm text-gray-300 mb-1">Aktuelles Passwort</label>
        <input type="password" value={current} onChange={e => setCurrent(e.target.value)} required
          className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-blue-500" />
      </div>
      <div>
        <label className="block text-sm text-gray-300 mb-1">Neues Passwort</label>
        <input type="password" value={next} onChange={e => setNext(e.target.value)} required minLength={8}
          className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-blue-500" />
      </div>
      <div>
        <label className="block text-sm text-gray-300 mb-1">Neues Passwort bestätigen</label>
        <input type="password" value={confirm} onChange={e => setConfirm(e.target.value)} required minLength={8}
          className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-blue-500" />
      </div>
      <button type="submit" disabled={busy} className="bg-blue-600 hover:bg-blue-700 disabled:bg-gray-600 text-white font-semibold px-4 py-2 rounded-lg">
        {busy ? 'Ändert…' : 'Passwort ändern'}
      </button>
      <Feedback msg={msg} />
    </form>
  );
};

const TelegramTab: React.FC = () => {
  const [info, setInfo] = useState<TelegramInfo | null>(null);
  const [chatId, setChatId] = useState('');
  const [enabled, setEnabled] = useState(true);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ kind: 'ok' | 'err'; text: string } | null>(null);

  useEffect(() => {
    (async () => {
      try {
        const r = await api<TelegramInfo>('/api/me/telegram');
        setInfo(r); setChatId(r.chat_id ?? ''); setEnabled(r.notifications_enabled);
      } catch (err: any) { setMsg({ kind: 'err', text: err?.message || 'Fehler' }); }
    })();
  }, []);

  const save = async (e: React.FormEvent) => {
    e.preventDefault(); setBusy(true); setMsg(null);
    try {
      const r = await api<{ chat_id: string | null; notifications_enabled: boolean }>('/api/me/telegram', {
        method: 'PUT', body: JSON.stringify({ chat_id: chatId || null, notifications_enabled: enabled }),
      });
      setInfo({ chat_id: r.chat_id, notifications_enabled: r.notifications_enabled });
      setMsg({ kind: 'ok', text: 'Gespeichert.' });
    } catch (err: any) { setMsg({ kind: 'err', text: err?.message || 'Fehler' }); }
    finally { setBusy(false); }
  };

  const sendTest = async () => {
    setBusy(true); setMsg(null);
    try { await api('/api/me/telegram/test', { method: 'POST' }); setMsg({ kind: 'ok', text: 'Test-Nachricht gesendet.' }); }
    catch (err: any) { setMsg({ kind: 'err', text: err?.message || 'Fehler' }); }
    finally { setBusy(false); }
  };

  return (
    <form onSubmit={save} className="space-y-4">
      <p className="text-sm text-gray-400">
        Hinterlege deine Telegram-Chat-ID, um Neuigkeiten aus dem Schul-Portal in{' '}
        <a className="text-blue-400" href="https://t.me/hsg_hw_bot" target="_blank" rel="noreferrer">@hsg_hw_bot</a> zu bekommen.
        Chat-ID erhältst du z.&nbsp;B. über{' '}
        <a className="text-blue-400" href="https://t.me/userinfobot" target="_blank" rel="noreferrer">@userinfobot</a>.
      </p>
      <div>
        <label className="block text-sm text-gray-300 mb-1">Telegram-Chat-ID</label>
        <input type="text" value={chatId} onChange={e => setChatId(e.target.value)} placeholder="z. B. 123456789"
          className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white font-mono focus:outline-none focus:border-blue-500" />
      </div>
      <label className="flex items-center gap-2 text-sm text-gray-300">
        <input type="checkbox" checked={enabled} onChange={e => setEnabled(e.target.checked)} className="accent-blue-500" />
        Benachrichtigungen aktivieren
      </label>
      <div className="flex gap-3 flex-wrap">
        <button type="submit" disabled={busy} className="bg-blue-600 hover:bg-blue-700 disabled:bg-gray-600 text-white font-semibold px-4 py-2 rounded-lg">Speichern</button>
        <button type="button" onClick={sendTest} disabled={busy || !info?.chat_id}
          className="bg-gray-700 hover:bg-gray-600 disabled:bg-gray-800 disabled:text-gray-500 text-white px-4 py-2 rounded-lg flex items-center gap-2">
          <Send className="w-4 h-4" /> Test-Nachricht
        </button>
      </div>
      <Feedback msg={msg} />
    </form>
  );
};

const IcalTab: React.FC = () => {
  const [info, setInfo] = useState<IcsInfo | null>(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ kind: 'ok' | 'err'; text: string } | null>(null);

  const load = async () => {
    try { setInfo(await api<IcsInfo>('/api/me/ics')); }
    catch (err: any) { setMsg({ kind: 'err', text: err?.message || 'Fehler' }); }
  };

  useEffect(() => { load(); }, []);

  const rotate = async () => {
    setBusy(true); setMsg(null);
    try { setInfo(await api<IcsInfo>('/api/me/ics/rotate', { method: 'POST' })); setMsg({ kind: 'ok', text: 'Neuer Token erzeugt. Bestehende Abos müssen neu eingetragen werden.' }); }
    catch (err: any) { setMsg({ kind: 'err', text: err?.message || 'Fehler' }); }
    finally { setBusy(false); }
  };

  const revoke = async () => {
    if (!window.confirm('Kalender-Abo wirklich deaktivieren? Bestehende Abonnenten verlieren den Zugriff.')) return;
    setBusy(true); setMsg(null);
    try { await api('/api/me/ics', { method: 'DELETE' }); await load(); setMsg({ kind: 'ok', text: 'Abo deaktiviert.' }); }
    catch (err: any) { setMsg({ kind: 'err', text: err?.message || 'Fehler' }); }
    finally { setBusy(false); }
  };

  const copy = async (text: string) => {
    try { await navigator.clipboard.writeText(text); setMsg({ kind: 'ok', text: 'URL kopiert.' }); }
    catch { setMsg({ kind: 'err', text: 'Clipboard nicht verfügbar.' }); }
  };

  return (
    <div className="space-y-5">
      <p className="text-sm text-gray-400">
        Abonniere deinen persönlichen Kalender in Apple Calendar, Google Calendar, Outlook oder Thunderbird.
        Enthalten sind: Schul-Termine (nur eigene Jahrgangsstufe), Hausaufgaben-Fälligkeiten und eigene TODOs.
      </p>

      {info?.token ? (
        <div className="space-y-3">
          <div>
            <label className="block text-sm text-gray-300 mb-1">Abonnement-URL</label>
            <div className="flex gap-2">
              <input readOnly value={info.url ?? ''} className="flex-1 px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-xs font-mono text-white" />
              <button onClick={() => copy(info.url ?? '')} className="bg-gray-700 hover:bg-gray-600 text-white px-3 py-2 rounded-lg flex items-center gap-1"><Copy className="w-4 h-4" /></button>
            </div>
          </div>
          <div>
            <label className="block text-sm text-gray-300 mb-1">Für iOS/macOS (webcal://)</label>
            <div className="flex gap-2">
              <input readOnly value={info.webcal_url ?? ''} className="flex-1 px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-xs font-mono text-white" />
              <button onClick={() => copy(info.webcal_url ?? '')} className="bg-gray-700 hover:bg-gray-600 text-white px-3 py-2 rounded-lg flex items-center gap-1"><Copy className="w-4 h-4" /></button>
            </div>
          </div>
          <div className="flex gap-3 flex-wrap">
            <button onClick={rotate} disabled={busy} className="bg-gray-700 hover:bg-gray-600 text-white px-3 py-2 rounded-lg flex items-center gap-2"><RefreshCw className="w-4 h-4" /> Token neu erzeugen</button>
            <button onClick={revoke} disabled={busy} className="bg-red-600/80 hover:bg-red-700 text-white px-3 py-2 rounded-lg">Deaktivieren</button>
          </div>
        </div>
      ) : (
        <button onClick={rotate} disabled={busy} className="bg-blue-600 hover:bg-blue-700 disabled:bg-gray-600 text-white font-semibold px-4 py-2 rounded-lg">
          Kalender-Abo aktivieren
        </button>
      )}

      <Feedback msg={msg} />
    </div>
  );
};

interface PairingCode { id: string; code: string; expires_at: string; used_at: string | null; created_at: string; }
interface ChildRel { relation_id: string; id: string; name: string; email: string; class: string | null; created_at: string; }
interface ParentRel { relation_id: string; id: string; name: string; email: string; created_at: string; }

const FamilyTab: React.FC = () => {
  const role = useAuthStore(s => s.user?.role);
  const [children, setChildren] = useState<ChildRel[]>([]);
  const [parents, setParents] = useState<ParentRel[]>([]);
  const [codes, setCodes] = useState<PairingCode[]>([]);
  const [enterCode, setEnterCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ kind: 'ok' | 'err'; text: string } | null>(null);

  const load = async () => {
    try {
      if (role === 'parent' || role === 'admin') {
        const r = await api<{ children: ChildRel[] }>('/api/me/children');
        setChildren(r.children);
      }
      if (role === 'student') {
        const [p, c] = await Promise.all([
          api<{ parents: ParentRel[] }>('/api/me/parents'),
          api<{ codes: PairingCode[] }>('/api/me/pairing-codes'),
        ]);
        setParents(p.parents);
        setCodes(c.codes);
      }
    } catch (err: any) {
      setMsg({ kind: 'err', text: err?.message || 'Fehler' });
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [role]);

  const generateCode = async () => {
    setBusy(true); setMsg(null);
    try {
      await api('/api/me/pairing-codes', { method: 'POST' });
      await load();
      setMsg({ kind: 'ok', text: 'Neuer Pairing-Code erstellt. Teile ihn deinen Eltern mit — gültig 1 Stunde.' });
    } catch (err: any) {
      setMsg({ kind: 'err', text: err?.message || 'Fehler' });
    } finally { setBusy(false); }
  };

  const revokeCode = async (id: string) => {
    setBusy(true); setMsg(null);
    try { await api(`/api/me/pairing-codes/${id}`, { method: 'DELETE' }); await load(); }
    catch (err: any) { setMsg({ kind: 'err', text: err?.message || 'Fehler' }); }
    finally { setBusy(false); }
  };

  const redeemCode = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!enterCode.trim()) return;
    setBusy(true); setMsg(null);
    try {
      await api('/api/me/redeem-pairing', { method: 'POST', body: JSON.stringify({ code: enterCode.trim() }) });
      setEnterCode('');
      setMsg({ kind: 'ok', text: 'Verknüpft. Dein Kind ist jetzt in deinem Eltern-Dashboard sichtbar.' });
      await load();
    } catch (err: any) {
      setMsg({ kind: 'err', text: err?.message || 'Fehler' });
    } finally { setBusy(false); }
  };

  const deleteRel = async (id: string, label: string) => {
    if (!window.confirm(`Verknüpfung zu "${label}" wirklich auflösen?`)) return;
    setBusy(true); setMsg(null);
    try { await api(`/api/me/relations/${id}`, { method: 'DELETE' }); await load(); setMsg({ kind: 'ok', text: 'Verknüpfung aufgelöst.' }); }
    catch (err: any) { setMsg({ kind: 'err', text: err?.message || 'Fehler' }); }
    finally { setBusy(false); }
  };

  const copyCode = async (code: string) => {
    try { await navigator.clipboard.writeText(code); setMsg({ kind: 'ok', text: 'Code kopiert.' }); }
    catch { setMsg({ kind: 'err', text: 'Clipboard nicht verfügbar.' }); }
  };

  return (
    <div className="space-y-6">
      {/* Student view */}
      {role === 'student' && (
        <>
          <section>
            <h3 className="font-semibold text-white mb-2 flex items-center gap-2"><KeyRound className="w-4 h-4 text-blue-400" /> Pairing-Code für Eltern</h3>
            <p className="text-sm text-gray-400 mb-3">
              Damit ein Eltern-Account deine Hausaufgaben/Noten einsehen kann,
              erzeuge hier einen Code und gib ihn weiter. Der Code ist 1 Stunde gültig und nur einmal nutzbar.
            </p>
            <button onClick={generateCode} disabled={busy}
              className="bg-blue-600 hover:bg-blue-700 disabled:bg-gray-600 text-white font-semibold px-4 py-2 rounded-lg">
              Neuen Code erzeugen
            </button>

            {codes.length > 0 && (
              <ul className="mt-4 space-y-2">
                {codes.map(c => {
                  const active = !c.used_at && new Date(c.expires_at) > new Date();
                  return (
                    <li key={c.id} className={`flex items-center gap-3 p-3 rounded-lg border ${active ? 'bg-gray-900/60 border-blue-500/40' : 'bg-gray-900/40 border-gray-700 opacity-60'}`}>
                      <code className="text-2xl font-mono tracking-widest text-white">{c.code}</code>
                      <div className="flex-1 text-xs text-gray-400">
                        {active ? (
                          <>gültig bis {new Date(c.expires_at).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })} Uhr</>
                        ) : c.used_at ? (
                          <>eingelöst {new Date(c.used_at).toLocaleString('de-DE', { dateStyle: 'short', timeStyle: 'short' })}</>
                        ) : (
                          <>abgelaufen</>
                        )}
                      </div>
                      {active && (
                        <>
                          <button onClick={() => copyCode(c.code)} className="text-gray-400 hover:text-white" title="Kopieren">
                            <Copy className="w-4 h-4" />
                          </button>
                          <button onClick={() => revokeCode(c.id)} disabled={busy} className="text-gray-400 hover:text-red-400" title="Widerrufen">
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </>
                      )}
                    </li>
                  );
                })}
              </ul>
            )}
          </section>

          <section>
            <h3 className="font-semibold text-white mb-2 flex items-center gap-2"><Users2 className="w-4 h-4 text-green-400" /> Verknüpfte Eltern</h3>
            {parents.length === 0 ? (
              <p className="text-gray-500 text-sm">Noch keine Eltern verknüpft.</p>
            ) : (
              <ul className="space-y-2">
                {parents.map(p => (
                  <li key={p.relation_id} className="flex items-center gap-3 p-3 bg-gray-900/60 border border-gray-700 rounded-lg">
                    <div className="flex-1">
                      <div className="text-white">{p.name}</div>
                      <div className="text-xs text-gray-400">{p.email}</div>
                    </div>
                    <button onClick={() => deleteRel(p.relation_id, p.name)} disabled={busy}
                      className="p-2 text-gray-400 hover:text-red-400 hover:bg-gray-800 rounded" title="Verknüpfung auflösen">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </section>
        </>
      )}

      {/* Parent view */}
      {(role === 'parent' || role === 'admin') && (
        <>
          <section>
            <h3 className="font-semibold text-white mb-2 flex items-center gap-2"><KeyRound className="w-4 h-4 text-blue-400" /> Kind verknüpfen</h3>
            <p className="text-sm text-gray-400 mb-3">
              Lass dir von deinem Kind einen Pairing-Code aus dessen Einstellungen geben und trage ihn hier ein.
            </p>
            <form onSubmit={redeemCode} className="flex gap-2 flex-wrap">
              <input type="text" value={enterCode} onChange={e => setEnterCode(e.target.value.toUpperCase())}
                placeholder="ABCD1234" maxLength={16}
                className="flex-1 min-w-[180px] px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white font-mono tracking-widest uppercase focus:outline-none focus:border-blue-500" />
              <button type="submit" disabled={busy || !enterCode.trim()}
                className="bg-blue-600 hover:bg-blue-700 disabled:bg-gray-600 text-white font-semibold px-4 py-2 rounded-lg">
                Einlösen
              </button>
            </form>
          </section>

          <section>
            <h3 className="font-semibold text-white mb-2 flex items-center gap-2"><Users2 className="w-4 h-4 text-green-400" /> Deine Kinder</h3>
            {children.length === 0 ? (
              <p className="text-gray-500 text-sm">Noch keine Kinder verknüpft.</p>
            ) : (
              <ul className="space-y-2">
                {children.map(c => (
                  <li key={c.relation_id} className="flex items-center gap-3 p-3 bg-gray-900/60 border border-gray-700 rounded-lg">
                    <div className="flex-1">
                      <div className="text-white">{c.name}{c.class && <span className="ml-2 text-xs bg-gray-700 px-1.5 py-0.5 rounded">{c.class}</span>}</div>
                      <div className="text-xs text-gray-400">{c.email}</div>
                    </div>
                    <button onClick={() => deleteRel(c.relation_id, c.name)} disabled={busy}
                      className="p-2 text-gray-400 hover:text-red-400 hover:bg-gray-800 rounded" title="Verknüpfung auflösen">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </section>
        </>
      )}

      {role !== 'parent' && role !== 'admin' && role !== 'student' && (
        <p className="text-gray-500 text-sm">Eltern-Kind-Verknüpfung steht nur für Schüler:innen- und Eltern-Accounts zur Verfügung.</p>
      )}

      <section className="bg-gray-900/60 border border-gray-700 rounded-lg p-4">
        <h3 className="font-semibold text-white mb-2 flex items-center gap-2"><Users2 className="w-4 h-4 text-blue-400" /> Wie funktioniert's?</h3>
        <ol className="list-decimal list-inside text-sm text-gray-300 space-y-1">
          <li>Dein Kind öffnet in seinem Account <em>Einstellungen → Eltern / Kind</em>.</li>
          <li>Dort klickt es auf <em>Neuen Code erzeugen</em> und teilt dir den 8-stelligen Code mit.</li>
          <li>Du trägst den Code hier oben unter <em>Kind verknüpfen</em> ein.</li>
          <li>Der Code ist 1 Stunde gültig und kann nur einmal eingelöst werden.</li>
        </ol>
      </section>

      <Feedback msg={msg} />
    </div>
  );
};

/* ========== Eltern-Portal Tab (parent/admin only) ========== */

interface EpCreds {
  linked: boolean;
  school_subdomain?: string;
  login_email?: string;
  last_sync_at?: string | null;
  last_sync_error?: string | null;
  last_sync_duration_ms?: number | null;
  auto_sync_enabled?: boolean;
}

const ElternPortalTab: React.FC = () => {
  const [info, setInfo] = useState<EpCreds | null>(null);
  const [subdomain, setSubdomain] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ kind: 'ok' | 'err'; text: string } | null>(null);

  const load = async () => {
    try {
      const r = await api<EpCreds>('/api/eltern-portal/credentials');
      setInfo(r);
      if (r.linked) {
        setSubdomain(r.school_subdomain || '');
        setEmail(r.login_email || '');
      }
    } catch (err: any) { setMsg({ kind: 'err', text: err?.message || 'Fehler' }); }
  };
  useEffect(() => { load(); }, []);

  const save = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!subdomain || !email || !password) {
      setMsg({ kind: 'err', text: 'Bitte alle Felder ausfüllen (Passwort neu eingeben).' });
      return;
    }
    setBusy(true); setMsg(null);
    try {
      await api('/api/eltern-portal/credentials', {
        method: 'PUT', body: JSON.stringify({ school_subdomain: subdomain, login_email: email, password }),
      });
      setPassword('');
      setMsg({ kind: 'ok', text: 'Zugangsdaten gespeichert. Jetzt auf der Eltern-Portal-Seite synchronisieren.' });
      await load();
    } catch (err: any) { setMsg({ kind: 'err', text: err?.message || 'Fehler' }); }
    finally { setBusy(false); }
  };

  const unlink = async () => {
    if (!window.confirm('Eltern-Portal-Verknüpfung wirklich entfernen?')) return;
    setBusy(true); setMsg(null);
    try {
      await api('/api/eltern-portal/credentials', { method: 'DELETE' });
      setSubdomain(''); setEmail(''); setPassword('');
      setMsg({ kind: 'ok', text: 'Verknüpfung entfernt.' });
      await load();
    } catch (err: any) { setMsg({ kind: 'err', text: err?.message || 'Fehler' }); }
    finally { setBusy(false); }
  };

  const toggleAutoSync = async (enabled: boolean) => {
    setBusy(true);
    try {
      await api('/api/eltern-portal/auto-sync', { method: 'POST', body: JSON.stringify({ enabled }) });
      await load();
    } catch (err: any) { setMsg({ kind: 'err', text: err?.message || 'Fehler' }); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-5">
      {info?.linked ? (
        <section>
          <div className="flex flex-wrap items-start justify-between gap-3 mb-3">
            <div>
              <h3 className="font-semibold text-white flex items-center gap-2"><Link2 className="w-4 h-4 text-blue-400" /> Verknüpft</h3>
              <p className="text-sm text-gray-400 mt-1">
                <code className="text-gray-200">{info.school_subdomain}.eltern-portal.org</code> als <code className="text-gray-200">{info.login_email}</code>
              </p>
              {info.last_sync_at && <p className="text-xs text-gray-500 mt-1">Letzte Synchronisation: {new Date(info.last_sync_at).toLocaleString('de-DE', { dateStyle: 'short', timeStyle: 'short' })}</p>}
              {info.last_sync_error && <p className="text-xs text-red-400 mt-1">⚠️ {info.last_sync_error}</p>}
            </div>
            <div className="flex flex-wrap gap-2">
              <label className="flex items-center gap-2 text-sm bg-gray-700 px-3 py-2 rounded-lg cursor-pointer">
                <input type="checkbox" checked={info.auto_sync_enabled ?? false} onChange={e => toggleAutoSync(e.target.checked)} className="accent-blue-500" />
                Auto-Sync (2×/Tag)
              </label>
              <button onClick={unlink} disabled={busy} className="bg-red-600/80 hover:bg-red-700 text-sm font-semibold px-3 py-2 rounded-lg flex items-center gap-2">
                <Trash2 className="w-4 h-4" /> Trennen
              </button>
            </div>
          </div>
        </section>
      ) : (
        <section className="bg-blue-500/10 border border-blue-500/40 rounded-lg p-4">
          <h3 className="font-semibold text-blue-200 flex items-center gap-2 mb-2"><Link2 className="w-4 h-4" /> Eltern-Portal verknüpfen</h3>
          <p className="text-sm text-blue-100/90">
            Trage hier deine Zugangsdaten für dein offizielles Eltern-Portal (eltern-portal.org) ein.
            Nach dem Speichern kannst du auf dem <Link to="/dashboard" className="underline">Dashboard</Link> die Synchronisation starten.
          </p>
        </section>
      )}

      <form onSubmit={save} className="space-y-4">
        <div>
          <label className="block text-sm text-gray-300 mb-1">Schul-Subdomain</label>
          <input type="text" value={subdomain} onChange={e => setSubdomain(e.target.value)}
            placeholder="z. B. hsgyhoe (in https://hsgyhoe.eltern-portal.org)"
            className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-blue-500" />
        </div>
        <div>
          <label className="block text-sm text-gray-300 mb-1">E-Mail (Eltern-Portal-Login)</label>
          <input type="email" value={email} onChange={e => setEmail(e.target.value)}
            className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-blue-500" />
        </div>
        <div>
          <label className="block text-sm text-gray-300 mb-1">Passwort</label>
          <input type="password" value={password} onChange={e => setPassword(e.target.value)}
            placeholder={info?.linked ? '••• (neu eingeben zum Aktualisieren)' : 'Portal-Passwort'}
            className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-blue-500" />
          <p className="text-xs text-gray-500 mt-1">Wird AES-verschlüsselt gespeichert und nur für den Sync verwendet.</p>
        </div>
        <button type="submit" disabled={busy} className="bg-blue-600 hover:bg-blue-700 disabled:bg-gray-600 text-white font-semibold px-4 py-2 rounded-lg">
          {busy ? 'Speichert…' : info?.linked ? 'Zugangsdaten aktualisieren' : 'Verknüpfen'}
        </button>
      </form>

      <Feedback msg={msg} />
    </div>
  );
};

const SettingsPage: React.FC = () => {
  const role = useAuthStore(s => s.user?.role);
  const [tab, setTab] = useState<Tab>('profile');
  const tabs = getTabs(role);
  return (
    <div className="min-h-screen bg-gray-900 text-white">
      <header className="bg-gray-800 border-b border-gray-700 px-4 sm:px-6 py-4 flex items-center gap-3 flex-wrap">
        <Link to="/dashboard" className="text-gray-400 hover:text-white flex items-center gap-1"><ArrowLeft className="w-4 h-4" /> Dashboard</Link>
        <Settings className="w-6 h-6 text-gray-400" />
        <h1 className="text-xl font-semibold">Einstellungen</h1>
      </header>

      <main className="max-w-3xl mx-auto p-4 sm:p-6">
        <nav className="flex gap-1 bg-gray-800 rounded-lg p-1 mb-6 overflow-x-auto">
          {tabs.map(t => (
            <button key={t.key} onClick={() => setTab(t.key)}
              className={`flex items-center gap-2 px-3 py-2 rounded-md text-sm whitespace-nowrap ${
                tab === t.key ? 'bg-gray-700 text-white' : 'text-gray-400 hover:text-white'
              }`}>
              {t.icon} {t.label}
            </button>
          ))}
        </nav>

        <section className="bg-gray-800 border border-gray-700 rounded-lg p-6">
          {tab === 'profile' && <ProfileTab />}
          {tab === 'password' && <PasswordTab />}
          {tab === 'family' && <FamilyTab />}
          {tab === 'eltern_portal' && <ElternPortalTab />}
          {tab === 'telegram' && <TelegramTab />}
          {tab === 'ical' && <IcalTab />}
        </section>
      </main>
    </div>
  );
};

export default SettingsPage;
